/**
 * Adjusts the root HTML document's font size based on the class of the event target.
 *
 * This function is typically used as an event handler for UI buttons that modify text size.
 * It performs the following actions:
 *
 * - Checks the `classList` of the event target to determine if the font size should be increased or decreased.
 * - Increases the font size by 1px if the class is "increase", up to a maximum of 32px.
 * - Decreases the font size by 1px if the class is "decrease", down to a minimum of 12px.
 * - Updates the `cursor` style of the "increase" and "decrease" buttons:
 *   - Sets to `"auto"` if the font size has reached its upper or lower bound.
 *   - Otherwise, sets to `"pointer"`.
 *
 * Note: Although the function defines an `increaseCount` variable to track the number of increments, it is re-initialized
 * on each function call and thus does not persist across invocations.
 *
 * @param {Event} el - The event object from a click or UI interaction.
 * 
 * @returns {void}
 */

export const changeTextSize = (el) => {
  // Global variable to keep track of increase count across function calls
  let increaseCount = 0;
  const html = document.documentElement;
  let fontSize = parseFloat(getComputedStyle(html).fontSize);
  const isIncreasing = el.target.classList.contains("increase");
  const maxFontSize = 32;
  const minFontSize = 12;

  // Calculate new font size
  const newFontSize = isIncreasing
    ? Math.min(fontSize + 1, maxFontSize)
    : Math.max(fontSize - 1, minFontSize);

  // Update count and apply new size
  if (newFontSize !== fontSize) {
    html.style.fontSize = `${newFontSize}px`;
    increaseCount = isIncreasing ? increaseCount + 1 : Math.max(increaseCount - 1, 0);
  }

  // Update button cursor styles
  (document.querySelector(".increase") as HTMLElement).style.cursor = newFontSize === maxFontSize ? "auto" : "pointer";
  (document.querySelector(".decrease") as HTMLElement).style.cursor = newFontSize === minFontSize ? "auto" : "pointer";
};

export const toggleSearch = () => {
  const searchBox = document.querySelector('.search-box')
  const header = document.querySelector('.site-header')
  if (!searchBox || !header) return
  searchBox.classList.toggle('active')
  header.classList.toggle('active')
  document.documentElement.classList.toggle('takeover-active')
}

export const isSearchBoxOpen = () => {
  const searchBox = document.querySelector('.search-box')
  const header = document.querySelector('.site-header')
  if (!searchBox || !header) return
  return (searchBox.classList.contains('active') && header.classList.contains('active') && document.documentElement.classList.contains('takeover-active'))
}

/**
 * Toggles the navigation menu state.
 * 
 * This function activates or deactivates the navigation menu, menu button, and related classes based on the `isActive` parameter.
 * @param {boolean} isActive - Indicates whether the navigation menu should be active. `true` to activate; `false` to deactivate.
 * 
 * @returns {void}
 */

import MenuIcon from '../../static/images/menu-icon.png';
import CloseIcon from '../../static/images/x.png';

export const toggleNav = (isActive) => {
  const navBtn = document.querySelector('button.menu-button');
  const navMenu = document.querySelector('nav.menu.wrapper');
  const header = document.querySelector('.site-header');
  const icon = document.querySelector('.menu-button img');

  // if we don't have a menu button, menu, or header, exit
  if (!navBtn || !navMenu || !header) return;

  // if we're not open, remove the active classes
  if (!isActive) {
    navBtn.classList.remove('nav-active');
    navMenu.classList.remove('active');
    header.classList.remove('takeover-active');
    document.documentElement.classList.remove('takeover-active');
    if (icon) {
      icon.setAttribute('src', MenuIcon);
      icon.setAttribute('alt', 'Menu Icon');
    }
  } else {
    navBtn.classList.add('nav-active');
    navMenu.classList.add('active');
    header.classList.add('takeover-active');
    document.documentElement.classList.add('takeover-active');
    if (icon) {
      icon.setAttribute('src', CloseIcon);
      icon.setAttribute('alt', 'Close Icon');
    }
  }
}

export const toggleSubnav = (el) => {
  const parent = el.target.parentElement
  parent.classList.toggle('is-open');
}

export const toggleForm = (el) => {
  // set the urls to change the form action to
  const plans = "https://shop.healthmarkets.com/en/about-me/plans-found";
  const agents = "/local-health-insurance-agent/search/";
  // set the parent form
  const parent = el.target.parentElement
  // if not already active...
  if (el.target.classList.contains('accented')) return
  // toggle first button state
  parent.firstChild.classList.toggle('accented')
  if (parent.firstChild.classList.contains('accented')) {
    parent.firstChild.querySelector('input[type=radio]').checked = true
    parent.firstChild.closest('form').action = plans
    document.querySelector('input#zipCode')?.setAttribute('name', 'zip')
  }
  // toggle second button state
  parent.firstChild.nextSibling.classList.toggle('accented')
  if (parent.firstChild.nextSibling.classList.contains('accented')) {
    parent.firstChild.nextSibling.querySelector('input[type=radio]').checked = true
    parent.firstChild.nextSibling.closest('form').action = agents
    document.querySelector('input#zipCode')?.setAttribute('name', 'query')
  }
}

export const fadeIn = (selector) => {
  const targets = document.querySelectorAll(selector)

  Object.keys(targets).map((i) => {
    window.addEventListener("scroll", () => {
      const scrollTop = window.pageYOffset
      const targetTop = targets[i].offsetTop

      if ((scrollTop + window.innerHeight) >= targetTop) {
        targets[i].classList.add('animate')
      }
    })
  });
}

export const getRandomPhotos = (obj) => {
  let arr = Array();

  Object.keys(obj).map(i => {
    arr.push(obj[i]);
  });

  for (let i = arr.length - 1; i > 0; i--) {
      let j = Math.floor(Math.random() * (i + 1));
      let temp = arr[i];
      arr[i] = arr[j];
      arr[j] = temp;
  }

  return arr;
}

export const getUrlData = () => {
  const qs = require('qs');
  const whitelist = [
    "_hm_cp",
    "_ga",
    "_fbp"
  ];
  let uri = '';
  let num = 0;

  // grab the data
  let cookie = document.cookie;

  // parse the data
  cookie = cookie.split(';');

  for (let i=0; i < cookie.length; i++) {
    var pair = cookie[i].split('=');

    whitelist.map(j => {
      if (pair[0].trim() === j) {
          uri = (num !== 0) ? '\&' + uri : uri;
        uri += cookie[i].trim();
        num++;
      }
    });
  }

  // return the data
  return uri;
}

export const setUrlData = (url) => {
  // grab the pieces
  const uri = getUrlData();

  // append the pieces to the url
  const link = url + (!url.includes('?') ? '?' : '\&') + uri;

  // return the url
  return link;
}

export const sendForm = (e) => {
  // prevent the form from submitting before we add our pieces
  e.preventDefault();
  const form = e.target;

  // get the zip code value
  const zipField = form.querySelector('#zipCode');

  // setup the data
  form.action = setUrlData(form.action) + '&zip=' + zipField.defaultValue;

  // send the form
  form.submit();
}

export const routeLink = (e) => {
  // prevent the link from navigating
  e.preventDefault();

  // get the tagname
  const tagname = e.target.localName;

  // if it's not 'a' find 'a'
  const element = (tagname !== 'A') ? getParent(e.target) : e.target;

  if (process.env.NODE_ENV === 'development') {
    window.location.assign(element.href);
    return;
  }

  if (element) {
    // get the target url
    const url = window.MarketingParameterTracking.generateURLWithMarketingParams(element.href);

    // send the user to that url
    window.location.assign(url);
  }
};

const getParent = (el) => {
  let parent = el;
  let num = 0;

  while(parent.tagName !== 'A' && num < 5) {
    parent = parent.parentElement;
    num++
  }

  return parent;
};